import { SmtpProvider } from './smtp-config.models';

/** Mirrors the backend `EmailMessageStatus`; the API sends enums as their integers. */
export enum EmailLogStatus {
  Sending = 1,
  Sent = 2,
  NotSent = 3,
  Failed = 4,
  Delayed = 5,
  Delivered = 6,
  Rejected = 7,
  Bounced = 8,
  Complained = 9
}

/** Mirrors the backend `EmailRecipientKind`. */
export enum EmailLogRecipientKind {
  To = 1,
  Cc = 2
}

/** Mirrors the backend `EmailEventType`. */
export enum EmailLogEventType {
  Send = 1,
  Delivery = 2,
  DeliveryDelay = 3,
  Bounce = 4,
  Complaint = 5,
  Reject = 6,
  RenderingFailure = 7
}

/** Mirrors the backend `EmailBounceKind`. */
export enum EmailLogBounceKind {
  Permanent = 1,
  Transient = 2,
  Undetermined = 3
}

export interface EmailLogListItem {
  id: string;
  /** When the provider accepted it, or when it was logged if it never was. Shifted to local time by the date interceptor. */
  sentAt: string;
  /** The single address, or "{n} recipients". */
  toSummary: string;
  recipientCount: number;
  subject: string;
  /** `null` for a send that named no action. */
  actionName: string | null;
  smtpConfigurationId: string | null;
  /** "FromName (FromEmail)", or "Default (appsettings)". */
  smtpLabel: string;
  provider: SmtpProvider;
  /** False when the account's provider reports no delivery events. */
  tracked: boolean;
  attachmentCount: number;
  status: EmailLogStatus;
}

export interface EmailLogRecipient {
  address: string;
  kind: EmailLogRecipientKind;
  status: EmailLogStatus;
  lastEventAt: string | null;
  detail: string | null;
}

export interface EmailLogEvent {
  type: EmailLogEventType;
  bounceKind: EmailLogBounceKind | null;
  occurredAt: string;
  detail: string | null;
  /** `null` when the event applies to every recipient. */
  recipientAddress: string | null;
}

export interface EmailLogDetails extends EmailLogListItem {
  fromAddress: string;
  providerMessageId: string | null;
  error: string | null;
  redirectedTo: string | null;
  recipients: EmailLogRecipient[];
  /** Oldest first. */
  events: EmailLogEvent[];
}

export interface EmailLogFilters {
  /** `yyyy-MM-dd`. `null` leaves that end of the range open. */
  fromDate: string | null;
  toDate: string | null;
  /** Matches the email's status or any one recipient's. */
  status: EmailLogStatus | null;
  actionName: string | null;
  smtpConfigurationId: string | null;
  /** Matches the subject or any recipient address. */
  search: string | null;
}

export interface EmailLogPage {
  items: EmailLogListItem[];
  totalCount: number;
}
